import { forEach, isEmpty, isNumber, mapValues, sortBy, upperFirst } from 'lodash';

export const dictionaryByParentApp = {
    yahoo: {
        'campaign group': 'campaign',
        group: 'campaign',
        campaign: 'line',
        campaigns: 'lines',
        'brand name': 'advertiser name',
        'marketing objective': 'objective',
        'bidding strategy': 'goal type',
        'conversion goal': 'online conversions',
        'conversion event': 'pixel rule',
        'days and hours': 'day parting',
        platform: 'device',
        'reach estimator': 'forecasting',
        'integral ad': 'integral ad',
        ad: 'creative',
    },
    realize: {
        'taboola ads': 'realize',
        ' taboola': ' realize',
        'taboola ': 'realize ',
    },
};

export const exclusionByParentApp = {
    realize: {
        'Taboola Select': true,
        'All Taboola Publishers': true,
        'Taboola Audience': true,
        'Taboola Data': true,
        'Taboola API': true,
        'Taboola Advertiser Data Use Policy': true,
        'Taboola Pixel': true,
    },
    yahoo: {
        'Served Ads': true,
    },
};

const COORDINATING_CONJUNCTIONS_SET = new Set(['and', 'but', 'or', 'nor', 'for', 'so', 'yet']);

const adjustCase = (oldWord, newWord) => {
    if (oldWord === oldWord.toUpperCase()) {
        return newWord.toUpperCase();
    }

    if (oldWord === upperFirst(oldWord)) {
        return upperFirst(newWord);
    }

    return newWord.toLowerCase();
};

const alignCases = (oldText, newText) => {
    let oldWords = oldText.split(' ');
    const newWords = newText.split(' ');

    if (oldWords.length > newWords.length) {
        oldWords = oldWords.filter(value => !COORDINATING_CONJUNCTIONS_SET.has(value));
    }

    return newWords
        .map((word, index) => {
            const oldWord = oldWords[index] || oldWords[0];
            return adjustCase(oldWord, word);
        })
        .join(' ');
};

const replace = (message, regex, newValue, placeholders) => {
    let match;
    let text = message;
    while ((match = regex.exec(text)) !== null) {
        const replacement = `{{${placeholders.length}}}`;
        const matchedWord = match[1];

        text = text.slice(0, match.index) + replacement + text.slice(match.index + matchedWord.length);
        regex.lastIndex = match.index + replacement.length;

        placeholders.push(alignCases(match[0], newValue));
    }
    regex.lastIndex = 0;

    return text;
};

const getRegExpStringByKey = key => `\\b(${key})('?s?)\\b`;

const getReplacer =
    ({ key, dictionary, exclusionOccurrences }) =>
    (...matchArgs) => {
        const originMatch = matchArgs[0];
        const matchIndex = matchArgs.find(value => isNumber(value));
        const newValue = dictionary[key];
        const hasExclusionCollision = exclusionOccurrences.some(exclusion => {
            const exclusionStartIndex = exclusion.index;
            const exclusionEndIndex = exclusion.index + exclusion[0]?.length - 1;
            const matchStartIndex = matchIndex;
            const matchEndIndex = matchIndex + originMatch?.length - 1;
            const noIntersections = exclusionStartIndex > matchEndIndex || exclusionEndIndex < matchStartIndex;

            return !noIntersections;
        });

        if (hasExclusionCollision) {
            return originMatch;
        }
        return alignCases(originMatch, newValue);
    };

export const translateTermsByParentApp = (messages, parentApp) => {
    const dictionary = dictionaryByParentApp[parentApp] ?? dictionaryByParentApp.realize;
    const exclusion = exclusionByParentApp[parentApp] ?? exclusionByParentApp.realize;

    if (!dictionary) {
        return messages;
    }

    const dictionaryKeys = sortBy(Object.keys(dictionary), 'length').reverse();
    const dictionaryKeyToRegexMap = mapValues(dictionary, (_, key) => new RegExp(getRegExpStringByKey(key), 'gi'));

    const exclusionKeys = Object.keys(exclusion);
    const exclusionKeyToRegexMap = mapValues(exclusion, (_, key) => new RegExp(getRegExpStringByKey(key), 'gi'));

    return mapValues(messages, currentMessage => {
        const placeholders = [];
        let message = currentMessage || '';

        if (typeof message !== 'string') {
            return message;
        }

        const exclusionOccurrences = exclusionKeys.reduce(
            (acc, key) => [...acc, ...message.matchAll(exclusionKeyToRegexMap[key])],
            []
        );

        forEach(dictionaryKeys, key => {
            const regex = dictionaryKeyToRegexMap[key];
            if (isEmpty(exclusionOccurrences)) {
                message = replace(message, regex, dictionary[key], placeholders);
                return;
            }

            const replacer = getReplacer({ key, dictionary, exclusionOccurrences });
            message = message.replace(regex, replacer);
        });

        message = message.replace(/\{\{\d+\}\}/g, match => {
            const index = match.replace(/{{|}}/g, '');
            return placeholders[index];
        });

        return message;
    });
};
